/**
 * domains/ai/thesis2/store.ts — MER Algo Footprints v7.1H
 *
 * Stateful in-memory + persisted store for StructuredThesis per symbol.
 * Pattern mirrors the existing thesisEngine.ts store.
 * Consumers subscribe to changes and read per-symbol state.
 */

import { StructuredThesis } from './types';

// ─── Store shape ──────────────────────────────────────────────────────────────

export interface SymbolThesis2 {
  symbol:         string;
  current:        StructuredThesis | null;
  previous:       StructuredThesis | null;
  lastUpdatedAt:  number | null;
  isStale:        boolean;
}

// ─── Module-level singleton ───────────────────────────────────────────────────

const store     = new Map<string, SymbolThesis2>();
const listeners = new Set<() => void>();
const STORAGE_KEY  = 'mer_thesis2_v1';
const STALE_MS     = 5 * 60 * 1000;   // 5 min
const RESTORE_WINDOW_MS = 8 * 60 * 60 * 1000; // 8 hours

function notify() { listeners.forEach(fn => fn()); }

// ─── Persistence ──────────────────────────────────────────────────────────────

function persist() {
  try {
    const obj: Record<string, SymbolThesis2> = {};
    store.forEach((v, k) => { obj[k] = v; });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch {}
}

function hydrate() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const obj = JSON.parse(raw) as Record<string, SymbolThesis2>;
    const cutoff = Date.now() - RESTORE_WINDOW_MS;
    Object.entries(obj).forEach(([sym, entry]) => {
      if (entry.lastUpdatedAt && entry.lastUpdatedAt > cutoff) {
        store.set(sym, { ...entry, isStale: true });
      }
    });
  } catch {}
}

hydrate();

// ─── Public API ───────────────────────────────────────────────────────────────

export function subscribe2(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getThesis2(symbol: string): SymbolThesis2 {
  if (!store.has(symbol)) {
    store.set(symbol, {
      symbol,
      current:       null,
      previous:      null,
      lastUpdatedAt: null,
      isStale:       true,
    });
  }
  return store.get(symbol)!;
}

export function applyThesis2(thesis: StructuredThesis): SymbolThesis2 {
  const existing = getThesis2(thesis.symbol);
  const updated: SymbolThesis2 = {
    symbol:        thesis.symbol,
    current:       thesis,
    previous:      existing.current,
    lastUpdatedAt: Date.now(),
    isStale:       false,
  };
  store.set(thesis.symbol, updated);
  persist();
  notify();
  return updated;
}

export function markStale2(symbol: string) {
  const t = getThesis2(symbol);
  store.set(symbol, { ...t, isStale: true });
  notify();
}

export function clearThesis2(symbol: string) {
  store.delete(symbol);
  persist();
  notify();
}

/** Staleness sweep — call on a timer */
export function checkStaleness2() {
  const now = Date.now();
  store.forEach((t, sym) => {
    if (!t.isStale && t.lastUpdatedAt && (now - t.lastUpdatedAt > STALE_MS)) {
      store.set(sym, { ...t, isStale: true });
      notify();
    }
  });
}
